import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FaCalendarAlt, FaMapMarkerAlt, FaClock, FaEye,
  FaCheckCircle, FaTimes, FaUsers, FaVideo,
  FaTrophy, FaHourglassHalf, FaExclamationTriangle, FaSync,
  FaDollarSign, FaUser, FaCreditCard, FaReceipt,
} from "react-icons/fa";

const PaidConferenceStatus = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConference, setSelectedConference] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchConferences = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/user-conferences`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("pressToken")}` } }
      );
      if (res.data.success) setConferences(res.data.data);
    } catch (e) { console.error("Error fetching paid conferences:", e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchConferences();
    const interval = setInterval(fetchConferences, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { setCurrentPage(1); }, [activeTab]);

  const isRunning = (s) => s === "approved" || s === "running" || s === "modified";

  const getFiltered = () => {
    switch (activeTab) {
      case "pending": return conferences.filter(c => c.status === "pending");
      case "running": return conferences.filter(c => isRunning(c.status));
      case "completed": return conferences.filter(c => c.status === "completed");
      case "rejected": return conferences.filter(c => c.status === "rejected");
      default: return conferences;
    }
  };

  const filtered = getFiltered();
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const statusInfo = (status) => {
    switch (status) {
      case "pending": return { bg: "bg-amber-100 text-amber-700", dot: "bg-amber-400", icon: FaHourglassHalf, label: "Pending" };
      case "approved":
      case "running": return { bg: "bg-blue-100 text-blue-700", dot: "bg-blue-400", icon: FaUsers, label: "Running" };
      case "modified": return { bg: "bg-purple-100 text-purple-700", dot: "bg-purple-400", icon: FaUsers, label: "Modified" };
      case "completed": return { bg: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-400", icon: FaTrophy, label: "Completed" };
      case "rejected": return { bg: "bg-red-100 text-red-700", dot: "bg-red-400", icon: FaTimes, label: "Rejected" };
      default: return { bg: "bg-slate-100 text-slate-600", dot: "bg-slate-400", icon: FaExclamationTriangle, label: "Unknown" };
    }
  };

  const tabs = [
    { id: "pending", label: "Pending", count: conferences.filter(c => c.status === "pending").length, color: "amber" },
    { id: "running", label: "Running", count: conferences.filter(c => isRunning(c.status)).length, color: "blue" },
    { id: "completed", label: "Done", count: conferences.filter(c => c.status === "completed").length, color: "emerald" },
    { id: "rejected", label: "Rejected", count: conferences.filter(c => c.status === "rejected").length, color: "red" },
  ];

  const tabActiveColor = {
    pending: "bg-amber-500 text-white shadow-amber-200",
    running: "bg-blue-500 text-white shadow-blue-200",
    completed: "bg-emerald-500 text-white shadow-emerald-200",
    rejected: "bg-red-500 text-white shadow-red-200",
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 pb-8 space-y-3">
        <div className="h-28 bg-white rounded-[1.5rem] animate-pulse" />
        <div className="grid grid-cols-4 gap-2">
          {[0, 1, 2, 3].map(i => <div key={i} className="h-16 bg-white rounded-2xl animate-pulse" />)}
        </div>
        {[0, 1, 2].map(i => <div key={i} className="h-28 bg-white rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 pb-8">

      {/* ── Hero Header ── */}
      <div className="relative bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-500 rounded-[1.5rem] p-4 sm:p-8 mb-4 overflow-hidden">
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/5" />
        <div className="absolute bottom-0 left-1/3 w-40 h-40 rounded-full bg-white/5 translate-y-1/2" />
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center flex-shrink-0">
              <FaDollarSign className="text-white w-5 h-5" />
            </div>
            <div>
              <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Paid Conference</p>
              <h1 className="text-lg sm:text-2xl font-black text-white tracking-tight leading-tight">Conference Status</h1>
              <p className="text-white/70 text-xs font-medium mt-0.5 hidden sm:block">Track your paid conferences</p>
            </div>
          </div>
          <button onClick={fetchConferences}
            className="w-9 h-9 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center text-white hover:bg-white/30 active:scale-95 transition-all flex-shrink-0">
            <FaSync className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* ── Stats Grid ── */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: "Pending", val: conferences.filter(c => c.status === "pending").length, light: "bg-amber-50 text-amber-600", icon: FaHourglassHalf },
          { label: "Running", val: conferences.filter(c => isRunning(c.status)).length, light: "bg-blue-50 text-blue-600", icon: FaUsers },
          { label: "Done", val: conferences.filter(c => c.status === "completed").length, light: "bg-emerald-50 text-emerald-600", icon: FaTrophy },
          { label: "Rejected", val: conferences.filter(c => c.status === "rejected").length, light: "bg-red-50 text-red-600", icon: FaTimes },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl p-2.5 sm:p-4 flex flex-col items-center text-center shadow-sm border border-slate-50">
            <div className={`w-7 h-7 rounded-lg ${s.light} flex items-center justify-center mb-1`}>
              <s.icon className="w-3 h-3" />
            </div>
            <p className="text-lg font-black text-slate-800 leading-none">{s.val}</p>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Tab Bar ── */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-0.5 scrollbar-hide">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-black whitespace-nowrap transition-all active:scale-95 shadow-sm flex-shrink-0 ${activeTab === tab.id
              ? `${tabActiveColor[tab.id]} shadow-md`
              : "bg-white text-slate-500 border border-slate-100"
              }`}>
            {tab.label}
            <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? "bg-white/20" : "bg-slate-100 text-slate-500"
              }`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* ── Conference Cards ── */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 flex flex-col items-center justify-center text-center shadow-sm border border-slate-50">
          <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mb-3">
            <FaCalendarAlt className="w-6 h-6 text-slate-300" />
          </div>
          <p className="text-sm font-black text-slate-600">No {tabs.find(t => t.id === activeTab)?.label} Paid Conferences</p>
          <p className="text-xs text-slate-400 mt-1 font-medium">No paid conferences in this category yet.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {paginated.map((conference) => {
            const si = statusInfo(conference.status);
            return (
              <div key={conference._id} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-50">

                {/* Card Header */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-black text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        #{conference.conferenceId}
                      </span>
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-1 ${si.bg}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${si.dot}`} />
                        {si.label}
                      </span>
                      {/* Payment badge */}
                      {conference.paymentStatus && (
                        <span className={`text-[9px] font-black px-2 py-0.5 rounded-full ${conference.paymentStatus === "paid"
                          ? "bg-emerald-50 text-emerald-600"
                          : "bg-slate-100 text-slate-500"
                          }`}>
                          {conference.paymentStatus === "paid" ? "✓ Paid" : "Pending Payment"}
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-black text-slate-800 leading-tight truncate">{conference.topic}</p>
                  </div>
                  <button onClick={() => { setSelectedConference(conference); setShowModal(true); }}
                    className="flex-shrink-0 w-8 h-8 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-500 hover:bg-amber-50 hover:text-amber-500 hover:border-amber-100 active:scale-95 transition-all">
                    <FaEye className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <FaCalendarAlt className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className="text-xs font-semibold truncate">{conference.conferenceDate}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <FaClock className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className="text-xs font-semibold">{conference.conferenceTime} {conference.timePeriod}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500 col-span-2">
                    <FaMapMarkerAlt className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className="text-xs font-semibold truncate">{conference.place}, {conference.city}, {conference.state}</span>
                  </div>
                </div>

                {/* Reporters strip (running) */}
                {isRunning(conference.status) && conference.acceptedReporters?.length > 0 && (
                  <div className="mt-2.5 flex items-center gap-2 bg-blue-50 rounded-xl px-3 py-2">
                    <FaUsers className="w-3 h-3 text-blue-500" />
                    <span className="text-xs font-black text-blue-700">{conference.acceptedReporters.length} Reporters</span>
                    <span className="text-[10px] text-blue-500 ml-auto">
                      {conference.totalProofSubmitted || 0} done · {conference.totalProofPending || 0} pending
                    </span>
                  </div>
                )}

                {/* Payment info strip */}
                {conference.paymentAmount > 0 && (
                  <div className="mt-2 flex items-center gap-2 bg-amber-50 rounded-xl px-3 py-1.5">
                    <FaReceipt className="w-3 h-3 text-amber-500" />
                    <span className="text-xs font-bold text-amber-700">₹{conference.paymentAmount}</span>
                    {conference.numberOfReporters && (
                      <span className="text-[10px] text-amber-500 ml-auto">{conference.numberOfReporters} reporter(s) booked</span>
                    )}
                  </div>
                )}

                {/* Rejection */}
                {conference.status === "rejected" && (conference.rejectReason || conference.adminAction?.note) && (
                  <div className="mt-2.5 bg-red-50 rounded-xl px-3 py-2 flex items-start gap-2">
                    <FaExclamationTriangle className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-700 font-medium line-clamp-2">
                      {conference.rejectReason || conference.adminAction?.note}
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
                className="px-4 py-2 text-xs font-black bg-white rounded-xl border border-slate-200 text-slate-600 disabled:opacity-40 active:scale-95 transition-all">
                ← Prev
              </button>
              <span className="text-xs font-bold text-slate-500">Page {currentPage} of {totalPages}</span>
              <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}
                className="px-4 py-2 text-xs font-black bg-white rounded-xl border border-slate-200 text-slate-600 disabled:opacity-40 active:scale-95 transition-all">
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Detail Bottom Sheet ── */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-t-[2rem] sm:rounded-[2rem] w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl z-10">

            {/* Modal Header */}
            <div className="sticky top-0 bg-white rounded-t-[2rem] px-5 py-4 border-b border-slate-100 flex items-start justify-between z-10">
              <div className="pr-8">
                <span className="text-[10px] font-black text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                  #{selectedConference.conferenceId}
                </span>
                <h3 className="text-sm font-black text-slate-800 mt-1 leading-tight">{selectedConference.topic}</h3>
              </div>
              <button onClick={() => setShowModal(false)}
                className="absolute right-4 top-4 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-all">
                <FaTimes className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="p-5 space-y-4">

              {/* Status + payment badge row */}
              <div className="flex items-center gap-2 flex-wrap">
                {(() => {
                  const si = statusInfo(selectedConference.status); return (
                    <span className={`inline-flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-full ${si.bg}`}>
                      <span className={`w-2 h-2 rounded-full ${si.dot}`} />
                      {si.label}
                    </span>
                  );
                })()}
                {selectedConference.paymentStatus && (
                  <span className={`text-xs font-black px-3 py-1.5 rounded-full ${selectedConference.paymentStatus === "paid"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-slate-100 text-slate-600"
                    }`}>
                    {selectedConference.paymentStatus === "paid" ? "✓ Payment Confirmed" : "Payment Pending"}
                  </span>
                )}
              </div>

              {/* Conference Details */}
              <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Conference Details</p>
                {[
                  { icon: FaCalendarAlt, label: "Date", val: selectedConference.conferenceDate },
                  { icon: FaClock, label: "Time", val: `${selectedConference.conferenceTime} ${selectedConference.timePeriod}` },
                  { icon: FaMapMarkerAlt, label: "Location", val: `${selectedConference.city}, ${selectedConference.state}` },
                  { icon: FaMapMarkerAlt, label: "Venue", val: selectedConference.place },
                ].map((r, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                      <r.icon className="w-3 h-3 text-slate-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[9px] font-bold text-slate-400 uppercase">{r.label}</p>
                      <p className="text-xs font-black text-slate-700 leading-snug">{r.val}</p>
                    </div>
                  </div>
                ))}
                {selectedConference.purpose && (
                  <div>
                    <p className="text-[9px] font-bold text-slate-400 uppercase mb-1">Purpose</p>
                    <p className="text-xs text-slate-600 leading-relaxed font-medium">{selectedConference.purpose}</p>
                  </div>
                )}
              </div>

              {/* Payment Info Block */}
              {(selectedConference.paymentAmount > 0 || selectedConference.numberOfReporters) && (
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4">
                  <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-3">Payment Info</p>
                  <div className="space-y-2">
                    {[
                      { label: "Reporters Booked", val: selectedConference.numberOfReporters || "N/A" },
                      { label: "Total Amount", val: selectedConference.paymentAmount ? `₹${selectedConference.paymentAmount}` : "N/A" },
                      { label: "Payment Status", val: selectedConference.paymentStatus || "N/A" },
                    ].map((r, i) => (
                      <div key={i} className="flex justify-between items-center">
                        <span className="text-xs text-amber-600 font-medium">{r.label}</span>
                        <span className="text-xs font-black text-amber-800">{r.val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Accepted Reporters */}
              {selectedConference.acceptedReporters?.length > 0 && (
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                    Reporters ({selectedConference.acceptedReporters.length})
                  </p>
                  {/* Summary Pills */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[
                      { label: "Total", val: selectedConference.acceptedReporters.length, color: "bg-blue-50 text-blue-700" },
                      { label: "Done", val: selectedConference.acceptedReporters.filter(r => r.proofSubmitted).length, color: "bg-emerald-50 text-emerald-700" },
                      { label: "Pending", val: selectedConference.acceptedReporters.filter(r => !r.proofSubmitted).length, color: "bg-amber-50 text-amber-700" },
                    ].map((s, i) => (
                      <div key={i} className={`${s.color} rounded-xl p-2.5 text-center`}>
                        <p className="text-base font-black leading-none">{s.val}</p>
                        <p className="text-[9px] font-bold uppercase mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Reporter Cards */}
                  <div className="space-y-2">
                    {selectedConference.acceptedReporters.map((r, i) => (
                      <div key={i} className="bg-slate-50 rounded-xl p-3 flex items-start gap-3">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center flex-shrink-0">
                          <FaUser className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-black text-slate-800 truncate">{r.reporterName}</p>
                          <p className="text-[10px] text-slate-500 truncate">{r.reporterEmail}</p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            <span className="text-[9px] font-black text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">{r.iinsafId}</span>
                            <span className="text-[9px] text-slate-400">{r.reporterCity}, {r.reporterState}</span>
                          </div>
                          {/* Channel info */}
                          {r.proofDetails?.channelName && (
                            <div className="mt-1.5 flex items-center gap-1.5">
                              <span className="text-[9px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">
                                {r.proofDetails.channelName}
                                {r.proofDetails.platform && ` · ${r.proofDetails.platform}`}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                          {r.proofSubmitted ? (
                            r.proofDetails?.videoLink ? (
                              <a href={r.proofDetails.videoLink} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 bg-emerald-50 text-emerald-600 px-2 py-1 rounded-lg text-[9px] font-black border border-emerald-100">
                                <FaVideo className="w-2.5 h-2.5" /> Proof
                              </a>
                            ) : (
                              <span className="text-[9px] font-black bg-emerald-50 text-emerald-600 px-2 py-1 rounded-lg flex items-center gap-1">
                                <FaCheckCircle className="w-2.5 h-2.5" /> Done
                              </span>
                            )
                          ) : (
                            <span className="text-[9px] font-black bg-amber-50 text-amber-600 px-2 py-1 rounded-lg flex items-center gap-1">
                              <FaHourglassHalf className="w-2.5 h-2.5" /> Pending
                            </span>
                          )}
                          {r.acceptedAt && (
                            <span className="text-[9px] text-slate-400">{new Date(r.acceptedAt).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No reporters yet (approved) */}
              {isRunning(selectedConference.status) &&
                (!selectedConference.acceptedReporters || selectedConference.acceptedReporters.length === 0) && (
                  <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex items-start gap-3">
                    <FaUsers className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-black text-amber-800">No Reporters Yet</p>
                      <p className="text-xs text-amber-600 mt-0.5 font-medium">Conference approved — waiting for reporters to accept.</p>
                    </div>
                  </div>
                )}

              {/* Completed */}
              {selectedConference.status === "completed" && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-start gap-3">
                  <FaTrophy className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-black text-emerald-800">Conference Completed 🎉</p>
                    <p className="text-xs text-emerald-600 mt-0.5 font-medium">All reporters have submitted their proof successfully.</p>
                    {selectedConference.completedAt && (
                      <p className="text-[10px] text-emerald-500 mt-1">
                        Completed on: {new Date(selectedConference.completedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Rejected */}
              {selectedConference.status === "rejected" && (
                <div className="space-y-2">
                  <div className="bg-red-50 border border-red-100 rounded-2xl p-4">
                    <div className="flex items-start gap-3">
                      <FaExclamationTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-black text-red-800">Conference Rejected</p>
                        <p className="text-xs text-red-600 mt-1 font-medium leading-relaxed">
                          {selectedConference.rejectReason || selectedConference.adminAction?.note || "No specific reason provided."}
                        </p>
                        {(selectedConference.rejectedAt || selectedConference.adminAction?.actionDate) && (
                          <p className="text-[10px] text-red-400 mt-1.5">
                            Rejected on: {new Date(selectedConference.rejectedAt || selectedConference.adminAction?.actionDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-2xl p-3">
                    <p className="text-xs font-black text-slate-600">What's Next?</p>
                    <p className="text-xs text-slate-500 mt-1 font-medium leading-relaxed">
                      Review the rejection reason and consider submitting a new paid conference with the necessary corrections.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Close Footer */}
            <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4">
              <button onClick={() => setShowModal(false)}
                className="w-full py-3 bg-slate-100 text-slate-700 rounded-2xl text-sm font-black active:scale-95 transition-all">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaidConferenceStatus;
